import { GeckoBinnedDataBuffer } from 'features/dataLayers/index'
import isNil from 'lodash/isNil'
import {
  contains,
  GeckoBinReading,
  GeckoComponentCoords,
  GeckoComponentDataFrame,
  GeckoComponentRect,
  GeckoMeasurement,
  GeckoMeasurementRange,
} from 'types'

/**
 * Returns an array of CDF values along with their coordinates within provided bounds
 */
export const getMultipleCdfBinReadings = ({
  cdf,
  bounds,
  binnedDataBuffer,
}: {
  cdf: GeckoComponentDataFrame | undefined
  bounds?: GeckoComponentRect | undefined
  binnedDataBuffer: GeckoBinnedDataBuffer | undefined
}): {
  readings: ReadonlyArray<GeckoBinReading> | undefined
  range: GeckoMeasurementRange | undefined
  mean: GeckoMeasurement | undefined
} => {
  if (!cdf || !binnedDataBuffer || !bounds)
    return { readings: undefined, range: undefined, mean: undefined }

  const componentID = cdf.tags.component
  const xUnit = cdf.data.geometry.x.unit
  const yUnit = cdf.data.geometry.y.unit
  const measurementUnit = cdf.data.params.measurement_unit

  const cdfXStart = cdf.data.geometry.start.x.num
  const cdfYStart = cdf.data.geometry.start.y.num
  const cdfBinWidth = cdf.data.binning.bin_size.width.toNumber(
    cdf.data.geometry.x.unit
  )
  const cdfBinHeight = cdf.data.binning.bin_size.height.toNumber(
    cdf.data.geometry.y.unit
  )

  const projectedBounds = bounds.project(cdf.data.geometry.space)

  const boundsBinXStart =
    (projectedBounds.start.x.num - cdfXStart) / cdfBinWidth
  const boundsBinXEnd = (projectedBounds.end.x.num - cdfXStart) / cdfBinWidth
  const boundsBinYStart =
    (projectedBounds.start.y.num - cdfYStart) / cdfBinHeight
  const boundsBinYEnd = (projectedBounds.end.y.num - cdfYStart) / cdfBinHeight

  let min = Number.POSITIVE_INFINITY
  let max = Number.NEGATIVE_INFINITY
  let sum = 0
  let count = 0
  const readings: GeckoBinReading[] = []

  for (let i = 0; i < binnedDataBuffer.values.length; i++) {
    const num = binnedDataBuffer.values.at(i)
    if (isNil(num) || Number.isNaN(num)) continue

    const binX = i % cdf.data.binning.x_bin_count
    const binY = Math.floor(i / cdf.data.binning.x_bin_count)

    if (
      !contains(
        boundsBinXStart,
        boundsBinYStart,
        boundsBinXEnd,
        boundsBinYEnd,
        binX + 0.5,
        binY + 0.5
      )
    )
      continue

    const value = GeckoMeasurement.CreateFromValue(num, measurementUnit)

    const xStartCoord = binX * cdfBinWidth + cdfXStart
    const yStartCoord = binY * cdfBinHeight + cdfYStart
    const xEndCoord = (binX + 1) * cdfBinWidth + cdfXStart
    const yEndCoord = (binY + 1) * cdfBinHeight + cdfYStart

    const binRect = GeckoComponentRect.CreateFromXYs(
      componentID,
      xStartCoord,
      yStartCoord,
      xEndCoord,
      yEndCoord,
      xUnit,
      yUnit
    )

    const binCoords = GeckoComponentCoords.CreateFromValues(
      componentID,
      xStartCoord + cdfBinWidth / 2,
      xUnit,
      yStartCoord + cdfBinHeight / 2,
      yUnit
    )

    readings.push({ value, binCoords, binRect })

    min = Math.min(min, num)
    max = Math.max(max, num)
    sum += num
    count++
  }

  if (count === 0) return { readings: [], range: undefined, mean: undefined }

  return {
    readings,
    range: GeckoMeasurementRange.CreateFromValues(min, max, measurementUnit),
    mean: GeckoMeasurement.CreateFromValue(sum / count, measurementUnit),
  }
}
